import React from "react";
import { Box, Typography, LinearProgress } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { PROJECT_PHASES } from "../../utils/projectPhases";

interface ProjectPhaseStepperProps {
  activePhaseIndex: number;
  phaseCompletions: number[];
  onPhaseClick?: (phaseIndex: number) => void;
}

export const ProjectPhaseStepper: React.FC<ProjectPhaseStepperProps> = ({
  activePhaseIndex,
  phaseCompletions,
  onPhaseClick,
}) => {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: { xs: 1, md: 3 },
        padding: { xs: "10px 12px", md: "16px 20px" },
        backgroundColor: "white",
        borderRadius: "12px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        marginBottom: { xs: 2, md: 3 },
      }}
    >
      {PROJECT_PHASES.map((phase, index) => {
        const isActive = activePhaseIndex === index;
        const isCompleted = phaseCompletions[index] === 100;
        const completion = phaseCompletions[index] || 0;

        return (
          <Box
            key={phase.id}
            onClick={() => onPhaseClick && onPhaseClick(index)}
            sx={{
              padding: "12px",
              borderRadius: "8px",
              backgroundColor: isActive
                ? `${phase.color}08`
                : "transparent",
              border: isActive ? `1px solid ${phase.color}20` : "1px solid transparent",
              cursor: onPhaseClick ? "pointer" : "default",
              transition: "all 0.3s",
              "&:hover": onPhaseClick
                ? {
                    transform: "scale(1.02)",
                    backgroundColor: isActive
                      ? `${phase.color}12`
                      : `${phase.color}05`,
                  }
                : {},
            }}
          >
            {/* Phase Header - Icon + Title */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 0.5, md: 1 },
                marginBottom: 1,
                flexWrap: { xs: "wrap", md: "nowrap" },
              }}
            >
              {/* Icon */}
              <Box
                sx={{
                  fontSize: { xs: "14px", md: "18px" },
                  filter: isActive
                    ? "none"
                    : isCompleted
                    ? "none"
                    : "grayscale(50%)",
                  transition: "all 0.3s",
                  flexShrink: 0,
                }}
              >
                {isCompleted ? (
                  <CheckCircleIcon
                    sx={{
                      color: phase.color,
                      fontSize: { xs: "16px", md: "20px" },
                    }}
                  />
                ) : (
                  phase.icon
                )}
              </Box>

              {/* Title */}
              <Typography
                sx={{
                  fontSize: { xs: "11px", md: "14px" },
                  fontWeight: isActive ? 600 : 500,
                  color: isActive
                    ? phase.color
                    : isCompleted
                    ? phase.color
                    : "#6b7280",
                  flex: 1,
                  transition: "all 0.2s",
                  lineHeight: 1.2,
                }}
              >
                {phase.title}
              </Typography>

              {/* Percentage */}
              <Typography
                sx={{
                  fontSize: { xs: "11px", md: "13px" },
                  fontWeight: 600,
                  color: isCompleted ? phase.color : "#9ca3af",
                  flexShrink: 0,
                }}
              >
                {completion}%
              </Typography>
            </Box>

            {/* Progress Bar */}
            <Box
              sx={{
                position: "relative",
                height: "8px",
                backgroundColor: "#e5e7eb",
                borderRadius: "4px",
                overflow: "hidden",
              }}
            >
              {/* Filled Progress */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  height: "100%",
                  width: `${completion}%`,
                  background: isCompleted
                    ? phase.color
                    : `linear-gradient(90deg, ${phase.color} 0%, ${phase.color}dd 100%)`,
                  borderRadius: "4px",
                  transition: "width 0.5s ease-in-out",
                  boxShadow: isActive
                    ? `0 0 8px ${phase.color}66`
                    : "none",
                }}
              />

              {/* Animated shimmer effect for active phase */}
              {isActive && completion < 100 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(90deg, transparent, ${phase.color}33, transparent)`,
                    animation: "shimmer 2s infinite",
                    "@keyframes shimmer": {
                      "0%": {
                        transform: "translateX(-100%)",
                      },
                      "100%": {
                        transform: "translateX(100%)",
                      },
                    },
                  }}
                />
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
